import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { DateTime } from 'luxon';
import { uniq } from 'lodash';
import { styled } from '@mui/material/styles';
import {
  Alert,
  AlertTitle,
  Box,
  FormHelperText,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ErrorOutline as ErrorOutlineIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  WarningAmber as WarningAmberIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';

import { PassengerAddress } from '../../../data/models/passenger.model';
import { TripLegType } from '../../../data/models/reservation.model';
import { TripPurpose } from '../../../data/models/trip-purpose.model';
import { TimeZone } from '../../../data/models/time-zone.model';
import { ServicePlan } from '../../../data/models/service-plan.model';
import { UserCategoryTypeBC } from '../../../data/models/user-category-type.model';
import {
  TransportationAttribute,
  TransportationAttributeGroup,
} from '../../../data/models/transportation-attribute.model';

import {
  useGoogleMaps,
  type DirectionsTravelResult,
} from '../../../contexts/GoogleMapsProvider';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useCanUpdateReservations } from '../../../hooks/permissions/useCanUpdateReservations';
import { useUserCategoryTypeBC } from '../../../hooks/useUserCategoryTypeBC';
import { useCareLevelMismatch } from '../../../hooks/useCareLevelMismatch';
import {
  usePortalName,
  usePortalNameFromClientBC,
} from '../../../hooks/usePortalName';
import { useFeatureMemberPreferences } from '../../../hooks/features/useFeatureMemberPreferences';
import {
  useFeatureAddressValidationClientPortal,
  useFeatureAddressValidationF1,
  useFeatureAddressValidationMemberPortal,
} from '../../../hooks/features/useFeatureAddressValidation';

import * as lookupSlice from '../../../data/lookup.slice';
import * as reservationSlice from '../../../data/reservation.slice';
import * as passengerSlice from '../../../data/passenger.slice';
import * as clientSlice from '../../../data/client.slice';

import dateUtils from '../../../services/DateUtils';
import reservationUtils from '../../../services/reservationUtils';

import MultiLegTitle from './MultiLegTitle';
import DateInput from '../../forms/DateInput';
import TimeInput from '../../forms/TimeInput';
import SelectMenu from '../../forms/SelectMenu';
import AddressInput from '../../forms/AddressInput';
import NoteInputWithVisibleThreshold from '../../forms/NoteInputWithVisibleThreshold';
import PhoneInput from '../../forms/PhoneInput';
import BooleanRadioButtonGroup from '../../forms/BooleanRadioButtonGroup';
import GurneyForm from './GurneyForm';
import tripLegUtils from '../../../services/triplegUtils';

import { MAX_HEIGHT, MAX_WEIGHT } from '@/data/constants';
import { MEMBER_PORTAL_EXCLUDED_PURPOSE_NAMES } from '@/data/constants';

const PREFIX = 'TripLegForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  widthDesktop75: `${PREFIX}-width-desktop-75`,
  widthDesktop50: `${PREFIX}-width-desktop-50`,
  row: `${PREFIX}-row`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  dateTime: `${PREFIX}-dateTime`,
  subctrl: `${PREFIX}-subctrl`,
  names: `${PREFIX}-names`,
  name: `${PREFIX}-name`,
  error: `${PREFIX}-error`,
  attributes: `${PREFIX}-attributes`,
  levelOfCare: `${PREFIX}-level-of-care`,
  empty: `${PREFIX}-empty`,
  standingOrder: `${PREFIX}-standingOrder`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginBottom: theme.spacing(2),
    paddingTop: 0,
    paddingBottom: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-around',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    flexGrow: 1,
  },

  [`& .${classes.widthDesktop75}`]: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
      '& .MuiFormControl-root': {
        width: '100%',
      },
      [`&.${classes.subctrl}`]: {
        width: `calc(100% - ${theme.spacing(4)})`,
      },
    },
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },

  [`& .${classes.widthDesktop50}`]: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
      '& .MuiFormControl-root': {
        width: '100%',
      },
      [`&.${classes.subctrl}`]: {
        width: `calc(100% - ${theme.spacing(4)})`,
      },
    },
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
  },

  [`& .${classes.title}`]: {
    display: 'flex',
    justifyContent: 'space-between',
  },

  [`& .${classes.subtitle}`]: {
    fontWeight: 600,
    fontSize: 18,
  },

  [`& .${classes.row}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },

  [`& .${classes.subctrl}`]: {
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.names}`]: {
    marginLeft: theme.spacing(4),
    display: 'flex',
    gap: theme.spacing(1),
  },

  [`& .${classes.name}`]: {
    flexGrow: 1,
  },

  [`& .${classes.dateTime}`]: {
    display: 'flex',
    gap: theme.spacing(2),
  },

  [`& .${classes.error}`]: {
    marginLeft: theme.spacing(2.5),
  },

  [`& .${classes.attributes}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },

  [`& .${classes.levelOfCare}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.empty}`]: {
    height: theme.spacing(2),
  },
  [`& .${classes.standingOrder}`]: {
    marginTop: theme.spacing(2),
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px !important',
  },
}));

const StyledMessage = styled(Alert)(({ theme }) => ({
  maxWidth: 400,
  transition: 'all 0.2s ease-in-out',
}));

interface TripLegFormProps {
  clientBC: string;
  servicePlans: ServicePlan[];
  isReservation: boolean;
  tripType: TripLegType;
  tripPurposes: TripPurpose[];
  transportationAttributes: TransportationAttribute[];
  timeZones: TimeZone[];
  index: number;
  formHook: UseFormReturn<any, any>;
  canDelete: boolean;
  isPortalReservation: boolean;
  passengerAddresses?: PassengerAddress[];
  deleteLeg: (idx: number) => void;
}

export default function TripLegForm({
  isReservation,
  tripType,
  tripPurposes,
  transportationAttributes,
  timeZones,
  index,
  formHook,
  canDelete,
  isPortalReservation,
  deleteLeg,
  passengerAddresses,
  clientBC,
}: TripLegFormProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const requiredFieldMessage = t('common.requiredField');
  const { core, places, getDirections } = useGoogleMaps();
  const canUpdateReservations = useCanUpdateReservations();
  const canBypassPickupOffset = !isPortalReservation && canUpdateReservations;
  const userCategoryType = useUserCategoryTypeBC();
  const isMemberPortal = userCategoryType === UserCategoryTypeBC.Passenger;
  const isAHC = usePortalNameFromClientBC(clientBC);
  const memberPreferencesEnabled = useFeatureMemberPreferences();
  const addressValidationF1Enabled = useFeatureAddressValidationF1();
  const addressValidationMemberPortalEnabled =
    useFeatureAddressValidationMemberPortal();
  const addressValidationClientPortalEnabled =
    useFeatureAddressValidationClientPortal();
  const portalName = usePortalName();

  const emptyAttribute: TransportationAttribute = useMemo(
    () => ({
      transportationAttributeCategoryBC: '',
      name: t('reservation.noAttribute'),
      description: '',
      isPassengerSelectable: true,
      isDefault: false,
    }),
    [t],
  );

  const clientContactName = 'Alignment Health Plan';
  const clientContactPhoneNumber = '(844) 445-2824';

  const transportationAttributeMap = useAppSelector(
    lookupSlice.selectTransportationAttributeMap,
  );
  const reverseTransportationAttributeMap = useAppSelector(
    lookupSlice.selectReverseTransportationAttributeMap,
  );
  const estimatedTripTime = useAppSelector(
    reservationSlice.selectEstimatedTripTime,
  );
  const passengerPreferences = useAppSelector(
    passengerSlice.selectCurrentPassengerPreferences,
  );
  const loadingReservationValidations = useAppSelector(
    reservationSlice.selectLoadingReservationValidations,
  );
  const loadedReservationValidations = useAppSelector(
    reservationSlice.selectLoadedReservationValidations,
  );
  const loadingReservationConfidence = useAppSelector(
    reservationSlice.selectLoadingReservationConfidence,
  );
  const loadedReservationConfidence = useAppSelector(
    reservationSlice.selectLoadedReservationConfidence,
  );
  const reservationValidations = useAppSelector(
    reservationSlice.selectReservationValidations,
  );
  const reservationConfidence = useAppSelector(
    reservationSlice.selectReservationConfidence,
  );

  const gurneyAttribute = useMemo(
    () => reverseTransportationAttributeMap['GURNEY'],
    [reverseTransportationAttributeMap],
  );
  const bariatricGurneyAttribute = useMemo(
    () => reverseTransportationAttributeMap['BARIATRIC_GURNEY'],
    [reverseTransportationAttributeMap],
  );

  const sortedTripPurposes = useMemo(() => {
    let purposeCodes = tripPurposes ?? [];
    if (isMemberPortal) {
      purposeCodes = purposeCodes.filter((p) => !MEMBER_PORTAL_EXCLUDED_PURPOSE_NAMES.includes(p.name?.toLowerCase() || ''));
    }
    return [...purposeCodes].sort((a, b) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
  }, [tripPurposes, isMemberPortal]);

  const {
    control,
    formState,
    watch,
    setValue,
    getValues,
    getFieldState,
    setError,
    clearErrors,
  } = formHook;
  const c2cAttribute = reverseTransportationAttributeMap['C2C'];
  const ambulatoryAttribute = reverseTransportationAttributeMap['AMBULATORY'];

  useEffect(() => {
    if (isAHC && isMemberPortal) {
      const currentAttributeLevel1 = getValues(
        `MultiLegTrips[${index}].AttributeLevel1`,
      );

      if (
        (currentAttributeLevel1 === null ||
          currentAttributeLevel1 === undefined) &&
        ambulatoryAttribute !== undefined
      ) {
        setValue(
          `MultiLegTrips[${index}].AttributeLevel1`,
          ambulatoryAttribute,
        );
      }
    }
  }, [
    c2cAttribute,
    ambulatoryAttribute,
    isAHC,
    isMemberPortal,
    getValues,
    setValue,
    index,
  ]);
  const [appointmentTimeUserSet, setAppointmentTimeUserSet] = useState(() => {
    const initial = getValues(
      `MultiLegTrips[${index}].AppointmentTime`,
    ) as DateTime | null;
    return initial != null && initial.isValid;
  });

  const [genericErrorMessages, setGenericErrorMessages] = useState<string[]>();

  const tripPurpose = watch(`MultiLegTrips[${index}].TripPurpose`);
  const willCall = watch(`MultiLegTrips[${index}].WillCall`);
  const roundTripWillCall = watch(`MultiLegTrips[${index}].RoundTripWillCall`);
  const previousWillCall =
    index > 0 ? watch(`MultiLegTrips[${index - 1}].WillCall`) : false;

  const pickupAddress = watch(`MultiLegTrips[${index}].PickupAddress`);
  const dropoffAddress = watch(`MultiLegTrips[${index}].DropoffAddress`);
  const appointmentTime = watch(`MultiLegTrips[${index}].AppointmentTime`);
  const pickupTime = watch(`MultiLegTrips[${index}].PickupTime`);
  const roundTripReturnTime = watch(
    `MultiLegTrips[${index}].RoundTripReturnTime`,
  );
  const pickupTimeAutoSetRef = useRef(false);
  const returnTimeAutoSetRef = useRef(false);
  const validations = useAppSelector(
    reservationSlice.selectReservationValidations,
  );
  const [pickupTimeZoneName, setPickupTimeZone] = useState<string>();
  const [dropoffTimeZone, setDropoffTimeZone] = useState<string>();

  const attributeLevel1 = watch(`MultiLegTrips[${index}].AttributeLevel1`);
  const attributeLevel2 = watch(`MultiLegTrips[${index}].AttributeLevel2`);
  const attributeLevel3 = watch(`MultiLegTrips[${index}].AttributeLevel3`);
  const attributeLevel4 = watch(`MultiLegTrips[${index}].AttributeLevel4`);
  const attributeLevel5 = watch(`MultiLegTrips[${index}].AttributeLevel5`);
  const [attributesLevel1, setAttributesLevel1] =
    useState<TransportationAttribute[]>();
  const [attributesLevel2, setAttributesLevel2] =
    useState<TransportationAttribute[]>();
  const [attributesLevel3, setAttributesLevel3] =
    useState<TransportationAttribute[]>();
  const [attributesLevel4, setAttributesLevel4] =
    useState<TransportationAttribute[]>();
  const [attributesLevel5, setAttributesLevel5] =
    useState<TransportationAttribute[]>();
  const [attributesLevel6, setAttributesLevel6] =
    useState<TransportationAttribute[]>();
  const isNonAmbulatory = useMemo(() => {
    if (!attributeLevel1) {
      return false;
    }

    const attributeCode = transportationAttributeMap?.[attributeLevel1];
    return attributeCode !== undefined && attributeCode !== 'AMBULATORY';
  }, [attributeLevel1, transportationAttributeMap]);
  const mobilityAttribute = watch(`MultiLegTrips[${index}].MobilityAttribute`);
  const [mobilityAttributes, setMobilityAttributes] =
    useState<TransportationAttribute[]>();

  const setChildAttributes = (
    parentAttribute: TransportationAttribute | undefined,
    setFn: Function,
  ) => {
    if (parentAttribute?.childTransportationAttributeCategoryBC) {
      const children = transportationAttributes.filter(
        (ta) =>
          ta.transportationAttributeCategoryBC ===
          parentAttribute.childTransportationAttributeCategoryBC,
      );
      setFn(children);
    } else {
      setFn(undefined);
    }
  };

  const setTouched = (name: string) => {
    setValue(name, getValues(name), {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const resetChildAttributes = (startLevel: number) => {
    const childAttributeFns = [
      setAttributesLevel1,
      setAttributesLevel2,
      setAttributesLevel3,
      setAttributesLevel4,
      setAttributesLevel5,
      setAttributesLevel6,
    ];
    for (var idx = startLevel - 1; idx < childAttributeFns.length; idx++) {
      childAttributeFns[idx](undefined);
      setValue(`MultiLegTrips[${index}].AttributeLevel${idx + 1}`, undefined);
    }
  };

  const [showGurneyForm, setShowGurneyForm] = useState<boolean>(
    gurneyAttribute === attributeLevel2 ||
      bariatricGurneyAttribute === attributeLevel2,
  );
  const handleAttributeChange = async (evt: any, startLevel: number) => {
    const attributeName = evt.target.name;
    const newAttributeValue = evt.target.value || '';
    setValue(attributeName, newAttributeValue);
    if (attributeName === `MultiLegTrips[${index}].AttributeLevel2`) {
      const isGurneySelected =
        newAttributeValue === gurneyAttribute ||
        newAttributeValue === bariatricGurneyAttribute;
      if (isGurneySelected) {
        const r2rUUID = transportationAttributes.find(
          (attr) => attr.passengerCareLevelBC === 'R2R',
        )?.transportationAttributeUUID;
        if (r2rUUID) {
          setValue(`MultiLegTrips[${index}].MobilityAttribute`, r2rUUID);
        }
      }
    }
    resetChildAttributes(startLevel + 1);
    await formHook.trigger();

    setTouched(`MultiLegTrips[${index}].PickupPhone`);

    const attr = transportationAttributes.find(
      (ta) => ta.transportationAttributeUUID === newAttributeValue,
    );
    if (!attr?.transportationAttributeUUID) {
      return;
    }

    const isGurney =
      attr.transportationAttributeUUID === gurneyAttribute ||
      attr.transportationAttributeUUID === bariatricGurneyAttribute;
    setShowGurneyForm(isGurney);
  };

  const handleAssistanceAttributeChange = async (evt: any) => {
    clearErrors(`MultiLegTrips[${index}].MobilityAttribute`);
    setValue(evt.target.name, evt.target.value || '');
    await formHook.trigger();
    setTouched(`MultiLegTrips[${index}].PickupPhone`);
  };

  useEffect(() => {
    if (transportationAttributes && !attributesLevel1) {
      const primaryAttributes = transportationAttributes.filter(
        (a) =>
          a.grouping === TransportationAttributeGroup.PRIMARY &&
          a.isPassengerSelectable,
      );
      setAttributesLevel1(primaryAttributes);
      const mobAttributes = transportationAttributes.filter(
        (a) =>
          a.transportationAttributeCategoryBC ===
          reservationUtils.MobilityAttributeBC,
      );
      setMobilityAttributes(mobAttributes);
    }
  }, [transportationAttributes]);

  useEffect(() => {
    if (willCall) {
      formHook.setValue(`MultiLegTrips[${index}].PickupTime`, null);
      formHook.setValue(`MultiLegTrips[${index}].RoundTripWillCall`, true);
      formHook.clearErrors(`MultiLegTrips[${index}].PickupTime`);
    }
  }, [willCall]);

  useEffect(() => {
    if (previousWillCall) {
      formHook.setValue(`MultiLegTrips[${index}].WillCall`, true);
    }
  }, [previousWillCall]);

  useEffect(() => {
    const attr = transportationAttributes.find(
      (ta) => ta.transportationAttributeUUID === attributeLevel1,
    );
    const isNotAmbulatory =
      attr?.transportationAttributeUUID &&
      transportationAttributeMap[attr.transportationAttributeUUID] !==
        'AMBULATORY';
    clearErrors(`MultiLegTrips[${index}].NonAmbulatoryError`);
    setChildAttributes(attr, setAttributesLevel2);
  }, [attributeLevel1, transportationAttributeMap]);

  useEffect(() => {
    if (!isNonAmbulatory) {
      clearErrors(`MultiLegTrips[${index}].AttributeLevel2`);
    }
  }, [clearErrors, index, isNonAmbulatory]);

  useEffect(() => {
    const attr = transportationAttributes.find(
      (ta) => ta.transportationAttributeUUID === attributeLevel2,
    );
    setChildAttributes(attr, setAttributesLevel3);
  }, [attributeLevel2]);

  useEffect(() => {
    const attr = transportationAttributes.find(
      (ta) => ta.transportationAttributeUUID === attributeLevel3,
    );
    setChildAttributes(attr, setAttributesLevel4);
  }, [attributeLevel3]);

  useEffect(() => {
    const attr = transportationAttributes.find(
      (ta) => ta.transportationAttributeUUID === attributeLevel4,
    );
    setChildAttributes(attr, setAttributesLevel5);
  }, [attributeLevel4]);

  useEffect(() => {
    const attr = transportationAttributes.find(
      (ta) => ta.transportationAttributeUUID === attributeLevel5,
    );
    setChildAttributes(attr, setAttributesLevel6);
  }, [attributeLevel5]);

  useEffect(() => {
    if (formState.errors.GenericError?.message) {
      const msg = formState.errors.GenericError.message as string;
      setGenericErrorMessages(uniq(msg.split('\n')));
    } else {
      setGenericErrorMessages(undefined);
    }
  }, [formHook.formState]);

  useEffect(() => {
    const tripLegUpdate = validations?.tripLegUpdates.find(
      (tl) => tl.tripLegSequence === index + 1,
    );
    const tz = tripLegUpdate?.pickUpAddress?.timeZone;
    const tzName = reservationUtils.getTimeZoneAbbrev(tz, timeZones);
    if (tzName && tzName !== pickupTimeZoneName) {
      formHook.setValue(`MultiLegTrips[${index}].PickupTimeZone`, tz || '');
      setPickupTimeZone(tzName);
    }
  }, [pickupAddress, validations, index]);

  useEffect(() => {
    const tripLegUpdate = validations?.tripLegUpdates.find(
      (tl) => tl.tripLegSequence === index + 1,
    );
    const tz = tripLegUpdate?.dropOffAddress?.timeZone;
    const tzName = reservationUtils.getTimeZoneAbbrev(tz, timeZones);
    if (tzName && tzName !== dropoffTimeZone) {
      formHook.setValue(
        `MultiLegTrips[${index}].AppointmentTimeZone`,
        tz || '',
      );
      formHook.setValue(
        `MultiLegTrips[${index}].RoundTripReturnTimeZone`,
        tz || '',
      );
      setDropoffTimeZone(tzName);
    }
  }, [dropoffAddress, validations, index]);

  useEffect(() => {
    if (pickupAddress && dropoffAddress) {
      dispatch(
        reservationSlice.getEstimatedTripTimeAsync(
          pickupAddress.description,
          dropoffAddress.description,
        ),
      );
    } else {
      dispatch(reservationSlice.setEstimatedTripTime(null));
      formHook.setValue(`MultiLegTrips[${index}].PickupTime`, undefined);
      pickupTimeAutoSetRef.current = false;
    }
  }, [pickupAddress, dropoffAddress]);

  useEffect(() => {
    if (
      (portalName && isMemberPortal && !addressValidationMemberPortalEnabled) ||
      (portalName &&
        !isMemberPortal &&
        !addressValidationClientPortalEnabled) ||
      (!portalName && !addressValidationF1Enabled)
    ) {
      return;
    }
    if (
      validations &&
      !loadingReservationValidations &&
      pickupAddress &&
      dropoffAddress
    ) {
      const validation = validations?.tripLegUpdates.find(
        (tl) => tl.tripLegSequence === index + 1,
      );
      if (validation?.pickUpAddress && validation?.dropOffAddress) {
        const confidencePayload = tripLegUtils.getTripLegConfidencePayload({
          pickUpAddress: validation.pickUpAddress,
          dropOffAddress: validation.dropOffAddress,
        });
        dispatch(
          reservationSlice.validateTripLegAddressAsync(
            index,
            confidencePayload,
          ),
        );
      }
    }
  }, [validations]);

  useEffect(() => {
    if (willCall) {
      pickupTimeAutoSetRef.current = false;
      return;
    }

    const pickupFieldState = getFieldState(
      `MultiLegTrips[${index}].PickupTime`,
    );
    const userTouchedPickup = pickupFieldState.isDirty;

    if (
      !appointmentTime?.isValid ||
      !estimatedTripTime ||
      !appointmentTimeUserSet
    ) {
      return;
    }

    const facilityTimeZone = dropoffTimeZone || dateUtils.WORK_TIME_ZONE;

    let basePickupDateTime = appointmentTime;
    if (
      dateUtils.shouldDefaultToPreviousDay(appointmentTime, facilityTimeZone)
    ) {
      basePickupDateTime = appointmentTime.minus({ days: 1 });
    }

    const calculatedPickup = basePickupDateTime.minus({
      minutes: estimatedTripTime.totalPickupTime,
    });

    if (!userTouchedPickup && pickupTime?.equals(calculatedPickup)) {
      pickupTimeAutoSetRef.current = true;
      return;
    }

    const appointmentFieldState = getFieldState(
      `MultiLegTrips[${index}].AppointmentTime`,
    );
    const userChangedAppointment = appointmentFieldState.isDirty;

    const shouldAutoPopulate =
      !userTouchedPickup &&
      (!pickupTime || pickupTimeAutoSetRef.current || userChangedAppointment);

    if (shouldAutoPopulate) {
      if (!pickupTime || !pickupTime.equals(calculatedPickup)) {
        formHook.setValue(
          `MultiLegTrips[${index}].PickupTime`,
          calculatedPickup,
          {
            shouldValidate: true,
          },
        );
      }
      pickupTimeAutoSetRef.current = true;
      return;
    }

    pickupTimeAutoSetRef.current = false;
  }, [
    appointmentTime,
    appointmentTimeUserSet,
    estimatedTripTime,
    getFieldState,
    index,
    pickupTime,
    willCall,
    dropoffTimeZone,
  ]);

  // Validate that return trip time is not before appointment time
  useEffect(() => {
    if (tripType !== TripLegType.RoundTrip) return;
    if (roundTripWillCall) return;
    if (!appointmentTimeUserSet) return;
    if (!roundTripReturnTime?.isValid || !appointmentTime?.isValid) return;

    const isValid = roundTripReturnTime >= appointmentTime;

    if (!isValid) {
      setError(`MultiLegTrips[${index}].RoundTripReturnTime`, {
        type: 'manual',
        message: t('reservation.returnDateBeforePickupError'),
      });
    } else {
      clearErrors(`MultiLegTrips[${index}].RoundTripReturnTime`);
    }
  }, [
    roundTripReturnTime,
    appointmentTime,
    appointmentTimeUserSet,
    tripType,
    roundTripWillCall,
    index,
    setError,
    clearErrors,
    t,
  ]);

  // Auto-populate Return Date to Appointment Date for Round Trips
  useEffect(() => {
    if (tripType !== TripLegType.RoundTrip) {
      returnTimeAutoSetRef.current = false;
      return;
    }
    if (roundTripWillCall) {
      returnTimeAutoSetRef.current = false;
      return;
    }
    if (!appointmentTimeUserSet) return;
    if (!appointmentTime?.isValid) return;

    const returnFieldState = getFieldState(
      `MultiLegTrips[${index}].RoundTripReturnTime`,
    );
    const userTouchedReturn = returnFieldState.isDirty;

    if (!userTouchedReturn && roundTripReturnTime?.equals(appointmentTime)) {
      returnTimeAutoSetRef.current = true;
      return;
    }

    const shouldAutoPopulate =
      !userTouchedReturn &&
      (!roundTripReturnTime?.isValid || returnTimeAutoSetRef.current);

    if (shouldAutoPopulate) {
      if (
        !roundTripReturnTime ||
        !roundTripReturnTime.equals(appointmentTime)
      ) {
        formHook.setValue(
          `MultiLegTrips[${index}].RoundTripReturnTime`,
          appointmentTime,
          { shouldValidate: true },
        );
      }
      returnTimeAutoSetRef.current = true;
      return;
    }

    returnTimeAutoSetRef.current = false;
  }, [
    appointmentTime,
    appointmentTimeUserSet,
    tripType,
    roundTripWillCall,
    roundTripReturnTime,
    getFieldState,
    index,
    formHook,
  ]);

  const [directions, setDirections] = useState<DirectionsTravelResult>();
  const getPlaceDetailsAndDirections = async () => {
    if (pickupAddress?.place_id && dropoffAddress?.place_id && core && places) {
      try {
        const pickupPlace = new places!.Place({
          id: pickupAddress.place_id,
          requestedLanguage: 'en',
        });
        await pickupPlace.fetchFields({
          fields: ['location'],
        });

        const dropoffPlace = new places!.Place({
          id: dropoffAddress.place_id,
          requestedLanguage: 'en',
        });
        await dropoffPlace.fetchFields({
          fields: ['location'],
        });

        const res = await getDirections(
          pickupPlace.location!,
          dropoffPlace.location!,
        );

        if (res) {
          setDirections(res);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to get place details', e);
      }
    }
  };

  useEffect(() => {
    getPlaceDetailsAndDirections();
  }, [pickupAddress, dropoffAddress, core, places, appointmentTime]);

  const isAppointmentTimeAllowedForPortal = useMemo(() => {
    return isMemberPortal
      ? reservationUtils.isAppointmentTimeAllowedForPortal(
          appointmentTime,
          portalName,
        )
      : true;
  }, [appointmentTime, portalName]);

  useEffect(() => {
    if (isAppointmentTimeAllowedForPortal) {
      clearErrors(`MultiLegTrips[${index}].AppointmentTimeError`);
    } else {
      setError(`MultiLegTrips[${index}].AppointmentTimeError`, {
        type: 'manual',
        message: `${t('reservation.appointmentTimeNotAllowedErrorMessage')} ${t('footer.phoneNumber')}`,
      });
    }
  }, [isAppointmentTimeAllowedForPortal]);
  const { distance, duration } = useMemo(() => {
    return {
      distance: directions?.routes?.[0]?.legs?.[0]?.distance,
      duration: directions?.routes?.[0]?.legs?.[0]?.duration,
    };
  }, [directions]);

  const getTimeZoneAbbrev = (
    tzCode: string | undefined,
  ): string | undefined => {
    if (!tzCode) return undefined;
    const dt = DateTime.now().setZone(tzCode);
    if (dt.isValid) {
      const abbr = dt.toFormat('ZZZZ');
      const tzMap: Record<string, string> = {
        EDT: 'ET',
        EST: 'ET',
        PDT: 'PT',
        PST: 'PT',
        CDT: 'CT',
        CST: 'CT',
        MDT: 'MT',
        MST: 'MT',
      };

      if (abbr) {
        return tzMap[abbr] || abbr;
      }
    }
    const tz = timeZones?.find((z) => z.ianaStandard === tzCode);
    return tz?.timeZoneName;
  };

  const roundTripButtons = [
    { label: t('reservation.pickTime'), value: false },
    { label: t('reservation.willCall'), value: true },
  ];

  const calculateDefaultTimeForAppointmentTimeControl = (
    currentAppointmentDateTime: DateTime,
  ) => {
    const now = DateTime.now().setZone(dateUtils.WORK_TIME_ZONE);
    const isToday = currentAppointmentDateTime.hasSame(now, 'day');

    if (isToday) {
      return now < now.startOf('day').plus({ hour: 8 })
        ? now.startOf('day').plus({ hour: 8 })
        : now.plus({ hours: 1 }).startOf('hour');
    }
    return currentAppointmentDateTime;
  };

  const updateTimePortionDueToDateChange = (
    updatedDateTime: DateTime | null,
  ) => {
    const currentAppointmentTime = getValues(
      `MultiLegTrips[${index}].AppointmentTime`,
    ) as DateTime | null;

    if (!updatedDateTime) {
      formHook.setValue(
        `MultiLegTrips[${index}].AppointmentTime`,
        updatedDateTime,
        { shouldValidate: true },
      );
      return;
    }

    const nextAppointmentTime = currentAppointmentTime?.isValid
      ? updatedDateTime.set({
          hour: currentAppointmentTime.hour,
          minute: currentAppointmentTime.minute,
          second: currentAppointmentTime.second,
          millisecond: currentAppointmentTime.millisecond,
        })
      : calculateDefaultTimeForAppointmentTimeControl(updatedDateTime);

    formHook.setValue(
      `MultiLegTrips[${index}].AppointmentTime`,
      nextAppointmentTime,
      { shouldValidate: true, shouldDirty: true },
    );
  };

  const handleAppointmentTimeSelect = (selectedTime: DateTime | null) => {
    if (!selectedTime) return;

    const current = getValues(
      `MultiLegTrips[${index}].AppointmentTime`,
    ) as DateTime | null;
    const appointmentDate = current?.isValid ? current : selectedTime;

    const combined = appointmentDate.set({
      hour: selectedTime.hour,
      minute: selectedTime.minute,
      second: 0,
      millisecond: 0,
    });

    formHook.setValue(`MultiLegTrips[${index}].AppointmentTime`, combined, {
      shouldValidate: true,
    });
    setAppointmentTimeUserSet(true);
  };

  const getMaximumPickupTime = () => {
    if (!appointmentTime) return null;
    if (
      !canBypassPickupOffset &&
      appointmentTime &&
      appointmentTime.isValid &&
      estimatedTripTime
    ) {
      const minutes = estimatedTripTime.totalPickupTime;
      return appointmentTime.minus({ minutes });
    }
    return appointmentTime;
  };

  const getError = (index: number, name: string) => {
    if (formState.errors.MultiLegTrips) {
      const trips = formState.errors.MultiLegTrips as any;
      if (trips.length > index) {
        const trip = trips[index];
        return trip?.[name]?.message ? t(`${trip[name].message}`) : undefined;
      }
    }
    return undefined;
  };

  const onGurneyFormFieldChange = (field: string, value: string) => {
    if (field === `MultiLegTrips[${index}].height`) {
      if (value && Number(value) > 74) {
        setValue(
          `MultiLegTrips[${index}].AttributeLevel2`,
          bariatricGurneyAttribute,
        );
        clearErrors(`MultiLegTrips[${index}].height`);
      }
      if (
        value &&
        Number(value) <= 74 &&
        getValues(`MultiLegTrips[${index}].weight`) <= 250
      ) {
        setValue(`MultiLegTrips[${index}].AttributeLevel2`, gurneyAttribute);
        clearErrors(`MultiLegTrips[${index}].height`);
      }
    }
    if (field === `MultiLegTrips[${index}].weight`) {
      if (value && Number(value) > 250) {
        setValue(
          `MultiLegTrips[${index}].AttributeLevel2`,
          bariatricGurneyAttribute,
        );
        clearErrors(`MultiLegTrips[${index}].weight`);
      }
      if (
        value &&
        Number(value) <= 250 &&
        getValues(`MultiLegTrips[${index}].height`) <= 74
      ) {
        setValue(`MultiLegTrips[${index}].AttributeLevel2`, gurneyAttribute);
        clearErrors(`MultiLegTrips[${index}].weight`);
      }
      // reverse that
    }
    if (field === `MultiLegTrips[${index}].AttributeLevel4`) {
      setValue(`MultiLegTrips[${index}].AttributeLevel4`, value || undefined);
    }
    if (field === `MultiLegTrips[${index}].AttributeLevel5`) {
      setValue(`MultiLegTrips[${index}].AttributeLevel5`, value || undefined);
    }
  };

  const isBariatricGurney = useMemo(() => {
    return attributeLevel2 === bariatricGurneyAttribute;
  }, [attributeLevel2, bariatricGurneyAttribute]);

  const isGurney = useMemo(() => {
    return attributeLevel2 === gurneyAttribute;
  }, [attributeLevel2, gurneyAttribute]);

  const isCareLevelMismatch = useCareLevelMismatch(
    {
      attributeLevel1,
      attributeLevel2,
      mobilityAttribute,
      transportationAttributes,
    },
    [attributeLevel1, attributeLevel2, mobilityAttribute],
  );

  // Check if user has deviated from AHC Member Portal defaults
  const hasDeviatedFromDefaults = useMemo(() => {
    if (!isAHC || !isMemberPortal) return false;
    if (!c2cAttribute || !ambulatoryAttribute) return false;

    // Check if mobility attribute (assistance) is NOT C2C
    const isNotC2C = mobilityAttribute && mobilityAttribute !== c2cAttribute;

    // Check if attribute level 1 (mobility) is NOT Ambulatory
    const isNotAmbulatory =
      attributeLevel1 && attributeLevel1 !== ambulatoryAttribute;

    const hasDeviated = isNotC2C || isNotAmbulatory;

    if (hasDeviated && memberPreferencesEnabled) {
      return isCareLevelMismatch;
    }

    return hasDeviated;
  }, [
    attributeLevel2,
    mobilityAttribute,
    passengerPreferences,
    mobilityAttributes,
    memberPreferencesEnabled,
    transportationAttributes,
    isCareLevelMismatch,
  ]);

  useEffect(() => {
    // For AHC Member Portal: Check if deviated from defaults (Ambulatory + C2C)
    if (isAHC && isMemberPortal && hasDeviatedFromDefaults) {
      setError(`MultiLegTrips[${index}].MobilityAttribute`, {
        type: 'manual',
        message: t('reservation.careLevelMismatchValidation'),
      });
    } else if (
      memberPreferencesEnabled &&
      isMemberPortal &&
      isCareLevelMismatch
    ) {
      // For other Member Portals: Check member preferences
      setError(`MultiLegTrips[${index}].MobilityAttribute`, {
        type: 'manual',
        message: t('reservation.careLevelMismatchValidation'),
      });
    } else {
      clearErrors(`MultiLegTrips[${index}].MobilityAttribute`);
    }
  }, [
    isCareLevelMismatch,
    hasDeviatedFromDefaults,
    isAHC,
    mobilityAttribute,
    attributeLevel1,
  ]);

  const height = watch(`MultiLegTrips[${index}].height`);
  const weight = watch(`MultiLegTrips[${index}].weight`);
  const heightNum = useMemo(() => Number(height), [height]);
  const weightNum = useMemo(() => Number(weight), [weight]);
  const exceedsStandardLimits = useMemo(
    () => heightNum > MAX_HEIGHT || weightNum > MAX_WEIGHT,
    [heightNum, weightNum],
  );
  const withinStandardLimits = useMemo(
    () => heightNum <= MAX_HEIGHT && weightNum <= MAX_WEIGHT,
    [heightNum, weightNum],
  );

  const { filteredAttributesLevel2, gurneyFilterMessage } = useMemo(() => {
    if (!attributesLevel2) {
      return { filteredAttributesLevel2: undefined, gurneyFilterMessage: null };
    }

    // If in member portal, hide Gurney and Bariatric Gurney options
    if (isMemberPortal) {
      const filtered = attributesLevel2.filter(
        (attr) =>
          attr.transportationAttributeUUID !== gurneyAttribute &&
          attr.transportationAttributeUUID !== bariatricGurneyAttribute,
      );
      return {
        filteredAttributesLevel2: filtered,
        gurneyFilterMessage: null,
      };
    }

    if (
      attributeLevel2 !== gurneyAttribute &&
      attributeLevel2 !== bariatricGurneyAttribute
    ) {
      return {
        filteredAttributesLevel2: attributesLevel2,
        gurneyFilterMessage: null,
      };
    }

    if (!heightNum || !weightNum) {
      return {
        filteredAttributesLevel2: attributesLevel2,
        gurneyFilterMessage: null,
      };
    }

    if (exceedsStandardLimits) {
      return {
        filteredAttributesLevel2: attributesLevel2.filter(
          (attr) => attr.transportationAttributeUUID !== gurneyAttribute,
        ),
        gurneyFilterMessage: t('reservation.standardGurneyUnavailable', {
          defaultValue:
            'Standard Gurney is not available. Height or weight exceeds limits (74" / 250 lbs).',
        }),
      };
    }

    if (withinStandardLimits) {
      return {
        filteredAttributesLevel2: attributesLevel2.filter(
          (attr) =>
            attr.transportationAttributeUUID !== bariatricGurneyAttribute,
        ),
        gurneyFilterMessage: t('reservation.bariatricGurneyUnavailable', {
          defaultValue:
            'Bariatric Gurney is not available. Height or weight within limits (74" / 250 lbs).',
        }),
      };
    }

    return {
      filteredAttributesLevel2: attributesLevel2,
      gurneyFilterMessage: null,
    };
  }, [
    attributesLevel2,
    gurneyAttribute,
    bariatricGurneyAttribute,
    isMemberPortal,
    exceedsStandardLimits,
    withinStandardLimits,
    t,
  ]);

  useEffect(() => {
    if (!heightNum || !weightNum) {
      return;
    }

    if (exceedsStandardLimits) {
      formHook.setValue(
        `MultiLegTrips[${index}].AttributeLevel2`,
        bariatricGurneyAttribute,
      );
    }

    if (withinStandardLimits) {
      formHook.setValue(
        `MultiLegTrips[${index}].AttributeLevel2`,
        gurneyAttribute,
      );
    }
  }, [
    gurneyAttribute,
    bariatricGurneyAttribute,
    exceedsStandardLimits,
    withinStandardLimits,
  ]);

  const displayInvalidF1DropoffAddress = useMemo(() => {
    if (portalName || isMemberPortal) return false;
    const confidenceScore = parseFloat(
      reservationConfidence[index]?.tripLegConfidence?.dropOffLocationConfidence
        ?.confidenceScore,
    );
    const shouldDisplayError =
      !portalName &&
      addressValidationF1Enabled &&
      loadedReservationConfidence &&
      !loadingReservationConfidence &&
      confidenceScore < 1;
    if (shouldDisplayError) {
      setError(`MultiLegTrips[${index}].DropoffAddress`, {
        type: 'manual',
        message: t('addressValidation.addressNotApprovedErrorMessage'),
      });
    } else {
      clearErrors(`MultiLegTrips[${index}].DropoffAddress`);
    }
    return shouldDisplayError;
  }, [
    portalName,
    addressValidationF1Enabled,
    loadedReservationConfidence,
    loadingReservationConfidence,
    reservationConfidence,
    index,
  ]);

  const displayInvalidMemberPortalDropoffAddress = useMemo(() => {
    if (!portalName || !isMemberPortal) return false;
    const confidenceScore = parseFloat(
      reservationConfidence[index]?.tripLegConfidence?.dropOffLocationConfidence
        ?.confidenceScore,
    );
    const shouldDisplayError =
      portalName &&
      isMemberPortal &&
      addressValidationMemberPortalEnabled &&
      loadedReservationConfidence &&
      !loadingReservationConfidence &&
      confidenceScore < 1;
    if (shouldDisplayError) {
      setError(`MultiLegTrips[${index}].DropoffAddress`, {
        type: 'manual',
        message: t('addressValidation.addressNotApprovedErrorMessage'),
      });
    } else {
      clearErrors(`MultiLegTrips[${index}].DropoffAddress`);
    }
    return shouldDisplayError;
  }, [
    portalName,
    isMemberPortal,
    addressValidationMemberPortalEnabled,
    loadedReservationConfidence,
    loadingReservationConfidence,
    reservationConfidence,
    index,
  ]);

  const displayInvalidClientPortalDropoffAddress = useMemo(() => {
    if (!portalName || isMemberPortal) return false;
    const confidenceScore = parseFloat(
      reservationConfidence[index]?.tripLegConfidence?.dropOffLocationConfidence
        ?.confidenceScore,
    );
    const shouldDisplayError =
      portalName &&
      !isMemberPortal &&
      addressValidationClientPortalEnabled &&
      loadedReservationConfidence &&
      !loadingReservationConfidence &&
      confidenceScore < 1;
    if (shouldDisplayError) {
      setError(`MultiLegTrips[${index}].DropoffAddress`, {
        type: 'manual',
        message: t('addressValidation.addressNotApprovedErrorMessage'),
      });
    } else {
      clearErrors(`MultiLegTrips[${index}].DropoffAddress`);
    }
    return shouldDisplayError;
  }, [
    portalName,
    !isMemberPortal,
    addressValidationClientPortalEnabled,
    loadedReservationConfidence,
    loadingReservationConfidence,
    reservationConfidence,
    index,
  ]);

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.form}>
        {tripType === TripLegType.MultiLeg && (
          <Box className={classes.title}>
            <MultiLegTitle legIndex={index} />
            {canDelete && (
              <IconButton
                data-testid="delete-leg"
                onClick={(evt) => deleteLeg(index)}
                color="default"
                size="large">
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            )}
          </Box>
        )}
        {genericErrorMessages?.map((msg: any, idx: number) => (
          <FormHelperText error key={idx}>
            {msg}
          </FormHelperText>
        ))}
        <Box className={classes.widthDesktop75}>
          <SelectMenu
            label={t('member.reasonForTrip')}
            name={`MultiLegTrips[${index}].TripPurpose`}
            values={sortedTripPurposes}
            valueAttr="purposeCodeUUID"
            labelAttr="name"
            required={true}
            control={control}
            errorMessage={getError(index, 'TripPurpose')}
            rules={{ required: requiredFieldMessage }}
          />
        </Box>
        {(tripPurpose || !isPortalReservation) && (
          <>
            <Box className={`${classes.dateTime} ${classes.widthDesktop75}`}>
              <DateInput
                label={t('member.apptDate')}
                name={`MultiLegTrips[${index}].AppointmentTime`}
                control={control}
                required={true}
                minDate={dateUtils.getDefaultMinTime()}
                onChange={updateTimePortionDueToDateChange}
                timezone={dateUtils.WORK_TIME_ZONE}
                inputWidth={400}
              />
              {!willCall && (
                <TimeInput
                  label={
                    dropoffTimeZone
                      ? `${t('member.apptTime')} (${dropoffTimeZone})`
                      : t('member.apptTime')
                  }
                  name={`MultiLegTrips[${index}].AppointmentTime`}
                  control={control}
                  required={true}
                  minTime={dateUtils.getDefaultMinTime(appointmentTime)}
                  timezone={dateUtils.WORK_TIME_ZONE}
                  displayValue={appointmentTimeUserSet ? undefined : null}
                  onTimeSelect={handleAppointmentTimeSelect}
                />
              )}
            </Box>
            {!isAppointmentTimeAllowedForPortal &&
              getError(index, 'AppointmentTimeError') && (
                <FormHelperText error>
                  {getError(index, 'AppointmentTimeError')}
                </FormHelperText>
              )}
            {isAppointmentTimeAllowedForPortal && (
              <Box className={classes.empty} />
            )}

            <Typography variant="h5" component="h3">
              {t('member.pickup')}
            </Typography>
            <Box className={classes.widthDesktop75}>
              <BooleanRadioButtonGroup
                name={`MultiLegTrips[${index}].WillCall`}
                control={control}
                trueText={t('member.optionCallTextWhenReady')}
                falseText={t('member.optionPickDateTime')}
                disabled={previousWillCall}
              />
            </Box>
            {!willCall && isPortalReservation && (
              <FormHelperText className={classes.subctrl}>
                {t('member.pickupDateTimeHelperText')}
              </FormHelperText>
            )}
            {!willCall && (
              <>
                <Box
                  className={`${classes.dateTime} ${classes.widthDesktop75} ${classes.subctrl}`}>
                  <DateInput
                    label={t('member.date')}
                    name={`MultiLegTrips[${index}].PickupTime`}
                    control={control}
                    required={true}
                    minDate={dateUtils.getDefaultMinTime()}
                    maxDate={appointmentTime}
                    timezone={dateUtils.WORK_TIME_ZONE}
                    readOnly={isMemberPortal || !pickupAddress || !dropoffAddress}
                  />
                  <TimeInput
                    label={
                      pickupTimeZoneName
                        ? `${t('member.time')} (${pickupTimeZoneName})`
                        : t('member.time')
                    }
                    name={`MultiLegTrips[${index}].PickupTime`}
                    control={control}
                    required={true}
                    minTime={dateUtils.getDefaultMinTime(appointmentTime)}
                    maxTime={dateUtils.getDefaultMaxTime({
                      currentDate: pickupTime,
                      maxDate: getMaximumPickupTime(),
                    })}
                    maxTimeErrorMessage={
                      !canBypassPickupOffset
                        ? t('reservation.pickupTimeOffsetError', {
                            minutes: estimatedTripTime?.totalPickupTime,
                          })
                        : undefined
                    }
                    timezone={dateUtils.WORK_TIME_ZONE}
                    readOnly={isMemberPortal || !pickupAddress || !dropoffAddress}
                  />
                </Box>
              </>
            )}
            {pickupAddress && dropoffAddress && duration && (
              <Typography>
                {t('reservation.estimatedTravelTime', {
                  time: duration.text,
                  distance: distance?.text ?? '',
                })}
              </Typography>
            )}
            {!willCall && getError(index, 'PickupTime') && (
              <FormHelperText error className={classes.error}>
                {getError(index, 'PickupTime')}
              </FormHelperText>
            )}
            <AddressInput
              name={`MultiLegTrips[${index}].PickupAddress`}
              label={t('member.address')}
              required={true}
              errorMessage={getError(index, 'PickupAddress')}
              watch={watch}
              setValue={setValue}
              passengerAddresses={passengerAddresses}
            />
            <Box className={classes.widthDesktop75}>
              <NoteInputWithVisibleThreshold
                name={`MultiLegTrips[${index}].PickupNote`}
                label={t('member.notes')}
                control={formHook.control}
                warningMessage={t('reservation.providerVisibleNoteLimit')}
              />
            </Box>
            <Box className={classes.widthDesktop50}>
              <PhoneInput
                label={t('member.phone')}
                name={`MultiLegTrips[${index}].PickupPhone`}
                extensionName={`MultiLegTrips[${index}].PickupPhoneExtension`}
                required={true}
                control={formHook.control}
                errors={formHook.formState.errors}
                formHook={formHook}
                disableDropdown={true}
              />
            </Box>

            <Box className={classes.empty} />
            <Typography variant="h5" component="h3">
              {t('member.dropOff')}
            </Typography>
            <AddressInput
              name={`MultiLegTrips[${index}].DropoffAddress`}
              label={t('member.address')}
              required={true}
              errorMessage={getError(index, 'DropoffAddress')}
              watch={watch}
              setValue={setValue}
              passengerAddresses={passengerAddresses}
            />
            {/* F1 Address Validation */}
            {displayInvalidF1DropoffAddress && (
              <>
                <StyledMessage
                  icon={<InfoOutlinedIcon color="warning" />}
                  severity={'warning'}>
                  <AlertTitle>
                    <strong>
                      {t('addressValidation.addressNotApprovedTitle')}
                    </strong>
                  </AlertTitle>
                  {t('addressValidation.addressNotApprovedMessage')}
                  <AlertTitle sx={{ fontSize: '14px', marginTop: '10px' }}>
                    <strong>
                      {t('addressValidation.addressNotApprovedSecondAttempt')}
                    </strong>
                  </AlertTitle>
                  <Trans
                    i18nKey="addressValidation.addressNotApprovedSecondAttemptMessage"
                    values={{
                      client_name: clientContactName,
                      phone_number: clientContactPhoneNumber,
                    }}
                    components={{ strong: <strong /> }}
                  />
                </StyledMessage>
              </>
            )}
            {/* Member Portal Address Validation */}
            {displayInvalidMemberPortalDropoffAddress && (
              <>
                <StyledMessage
                  icon={<InfoOutlinedIcon color="warning" />}
                  severity={'warning'}>
                  <AlertTitle>
                    <strong>
                      {t('addressValidation.addressNotApprovedTitle')}
                    </strong>
                  </AlertTitle>
                  <Trans
                    i18nKey="addressValidation.addressNeedsApprovedLocationMessage"
                    values={{
                      client_name: clientContactName,
                      phone_number: clientContactPhoneNumber,
                    }}
                    components={{ strong: <strong /> }}
                  />
                </StyledMessage>
              </>
            )}

            {/* Client Portal Address Validation */}
            {displayInvalidClientPortalDropoffAddress && (
              <>
                <StyledMessage
                  icon={<InfoOutlinedIcon color="warning" />}
                  severity={'warning'}>
                  <AlertTitle>
                    <strong>
                      {t('addressValidation.addressLowConfidenceTitle')}
                    </strong>
                  </AlertTitle>
                  {t('addressValidation.addressLowConfidenceMessage')}
                </StyledMessage>
              </>
            )}
            <Box className={classes.widthDesktop75}>
              <NoteInputWithVisibleThreshold
                name={`MultiLegTrips[${index}].DropoffNote`}
                label={t('member.notes')}
                control={formHook.control}
                warningMessage={t('reservation.providerVisibleNoteLimit')}
              />
            </Box>
            <Box className={classes.widthDesktop50}>
              <PhoneInput
                label={t('member.phone')}
                name={`MultiLegTrips[${index}].DropoffPhone`}
                extensionName={`MultiLegTrips[${index}].DropoffPhoneExtension`}
                required={false}
                control={formHook.control}
                formHook={formHook}
                errors={formHook.formState.errors}
                disableDropdown={true}
              />
            </Box>
            {tripType === TripLegType.RoundTrip && (
              <>
                <Box className={classes.empty} />
                <Typography variant="h5" component="h3">
                  {t('reservation.returnTrip')}
                </Typography>
                <FormHelperText>
                  {t('member.whenYouReadyToPickedUp')}
                  <Box component="span" color={theme.palette.error.main}>
                    *
                  </Box>
                </FormHelperText>
                <Box className={classes.widthDesktop75}>
                  <BooleanRadioButtonGroup
                    name={`MultiLegTrips[${index}].RoundTripWillCall`}
                    control={control}
                    trueText={t('member.optionCallTextWhenReady')}
                    falseText={t('member.optionPickDateTime')}
                    disabled={willCall}
                  />
                </Box>
                {!roundTripWillCall && (
                  <>
                    <Box
                      className={`${classes.dateTime} ${classes.widthDesktop75} ${classes.subctrl}`}>
                      <DateInput
                        label={t('member.date')}
                        name={`MultiLegTrips[${index}].RoundTripReturnTime`}
                        control={control}
                        required={true}
                        errors={formState.errors}
                        minDate={
                          appointmentTime?.isValid
                            ? appointmentTime.startOf('day')
                            : dateUtils.getDefaultMinTime()
                        }
                        timezone={dateUtils.WORK_TIME_ZONE}
                      />
                      <TimeInput
                        label={
                          dropoffTimeZone
                            ? `${t('member.time')} (${dropoffTimeZone})`
                            : t('member.time')
                        }
                        name={`MultiLegTrips[${index}].RoundTripReturnTime`}
                        control={control}
                        required={true}
                        minTime={
                          roundTripReturnTime?.isValid &&
                          appointmentTime?.isValid &&
                          roundTripReturnTime.hasSame(appointmentTime, 'day')
                            ? appointmentTime
                            : dateUtils.getDefaultMinTime(roundTripReturnTime)
                        }
                        timezone={dateUtils.WORK_TIME_ZONE}
                      />
                    </Box>
                    {getError(index, 'RoundTripReturnTime') && (
                      <FormHelperText error className={classes.error}>
                        {getError(index, 'RoundTripReturnTime')}
                      </FormHelperText>
                    )}
                  </>
                )}
              </>
            )}
            <Box className={classes.empty} />
            <Typography variant="h5" component="h3">
              {t('member.levelOfCare')}
            </Typography>
            <Box className={`${classes.levelOfCare} ${classes.widthDesktop75}`}>
              <fieldset className={classes.attributes}>
                <legend>{t('reservation.passengerMobility')}</legend>
                {attributesLevel1 && (
                  <SelectMenu
                    label={t('reservation.selectAttribute')}
                    name={`MultiLegTrips[${index}].AttributeLevel1`}
                    values={attributesLevel1}
                    valueAttr="transportationAttributeUUID"
                    labelAttr="name"
                    required={true}
                    control={control}
                    rules={{ required: requiredFieldMessage }}
                    onChange={(evt) => handleAttributeChange(evt, 1)}
                  />
                )}
                {getError(index, 'NonAmbulatoryError') && (
                  <FormHelperText error>
                    {getError(index, 'NonAmbulatoryError')}
                  </FormHelperText>
                )}
                {filteredAttributesLevel2 && (
                  <>
                    <SelectMenu
                      label={t('reservation.selectAttribute')}
                      name={`MultiLegTrips[${index}].AttributeLevel2`}
                      values={[emptyAttribute, ...filteredAttributesLevel2]}
                      valueAttr="transportationAttributeUUID"
                      labelAttr="name"
                      required={isNonAmbulatory}
                      control={control}
                      rules={
                        isNonAmbulatory
                          ? { required: requiredFieldMessage }
                          : undefined
                      }
                      onChange={(evt) => handleAttributeChange(evt, 2)}
                    />
                    {gurneyFilterMessage && (
                      <FormHelperText sx={{ ml: 2, color: 'text.secondary' }}>
                        {gurneyFilterMessage}
                      </FormHelperText>
                    )}
                  </>
                )}
                {!isGurney && !isBariatricGurney && attributesLevel3 && (
                  <SelectMenu
                    label={t('reservation.selectAttribute')}
                    name={`MultiLegTrips[${index}].AttributeLevel3`}
                    values={[emptyAttribute, ...attributesLevel3]}
                    valueAttr="transportationAttributeUUID"
                    labelAttr="name"
                    required={false}
                    control={control}
                    onChange={(evt) => handleAttributeChange(evt, 3)}
                  />
                )}
                {attributesLevel4 && (
                  <SelectMenu
                    label={t('reservation.selectAttribute')}
                    name={`MultiLegTrips[${index}].AttributeLevel4`}
                    values={[emptyAttribute, ...attributesLevel4]}
                    valueAttr="transportationAttributeUUID"
                    labelAttr="name"
                    required={false}
                    control={control}
                    onChange={(evt) => handleAttributeChange(evt, 4)}
                  />
                )}
                {attributesLevel5 && (
                  <SelectMenu
                    label={t('reservation.selectAttribute')}
                    name={`MultiLegTrips[${index}].AttributeLevel5`}
                    values={[emptyAttribute, ...attributesLevel5]}
                    valueAttr="transportationAttributeUUID"
                    labelAttr="name"
                    required={false}
                    control={control}
                    onChange={(evt) => handleAttributeChange(evt, 5)}
                  />
                )}
                {attributesLevel6 && (
                  <SelectMenu
                    label={t('reservation.selectAttribute')}
                    name={`MultiLegTrips[${index}].AttributeLevel6`}
                    values={[emptyAttribute, ...attributesLevel6]}
                    valueAttr="transportationAttributeUUID"
                    labelAttr="name"
                    required={false}
                    control={control}
                    onChange={(evt) => handleAttributeChange(evt, 6)}
                  />
                )}
                {showGurneyForm && (
                  <GurneyForm
                    form={formHook}
                    onFieldChange={onGurneyFormFieldChange}
                    index={index}
                  />
                )}
              </fieldset>
              {mobilityAttributes && (
                <>
                  <SelectMenu
                    label={t('reservation.passengerAssistance')}
                    name={`MultiLegTrips[${index}].MobilityAttribute`}
                    values={mobilityAttributes}
                    valueAttr="transportationAttributeUUID"
                    labelAttr="name"
                    required={true}
                    control={control}
                    errorMessage={getError(index, 'MobilityAttribute')}
                    disabled={isGurney || isBariatricGurney}
                    rules={{ required: requiredFieldMessage }}
                    onChange={handleAssistanceAttributeChange}
                  />
                  {(isBariatricGurney || isGurney) && (
                    <>
                      <StyledMessage
                        icon={<WarningAmberIcon color="error" />}
                        severity={'error'}>
                        <AlertTitle>
                          <strong>{t('reservation.needsApproval')}</strong>
                        </AlertTitle>
                        {t('reservation.beriaticGurneyApprovalMessage')}
                      </StyledMessage>
                    </>
                  )}
                  {!!mobilityAttribute &&
                    (hasDeviatedFromDefaults || isCareLevelMismatch) && (
                      <StyledMessage
                        icon={
                          isMemberPortal ? (
                            <ErrorOutlineIcon color="warning" />
                          ) : (
                            <ErrorOutlineIcon color="info" />
                          )
                        }
                        severity={isMemberPortal ? 'warning' : 'info'}>
                        <AlertTitle>
                          <strong>
                            {isMemberPortal
                              ? t('reservation.careLevelMismatchTitle')
                              : t('reservation.careLevelMismatchTitleAdmin')}
                          </strong>
                          <br />
                          {isMemberPortal
                            ? t('reservation.careLevelMismatchMessage')
                            : t('reservation.careLevelMismatchMessageAdmin')}
                        </AlertTitle>
                      </StyledMessage>
                    )}
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </StyledBox>
  );
}
